import { PauseCircle, RotateCw } from 'lucide-react';
import { Outlet } from 'react-router';

import { Button } from '@/components/ui/Button';
import { Container } from '@/components/ui/Container';
import type { PausedFlag } from '@/features/system/api/system';
import { usePausedFlags } from '@/features/system/hooks/usePausedFlags';
import { maintenanceTime } from '@/features/system/maintenance';
import { useT } from '@/lib/i18n/context';

/**
 * Route guard for an area the platform has switched off during an incident.
 *
 * The flag's twin of `RequireEntitlement`, and the reverse of its message: a
 * plan wall says "upgrade", this says "we are on it, nothing is lost". The
 * poll behind `usePausedFlags` brings the page back on its own once the switch
 * is on again. The API refuses the work regardless; this only stops a merchant
 * being shown tools that would end in a 503.
 */
export function RequireFlag({ flag, label }: { flag: string; label: string }) {
  const { paused } = usePausedFlags();
  const entry = paused(flag);
  if (!entry) return <Outlet />;
  return <FeaturePausedScreen entry={entry} label={label} />;
}

export function FeaturePausedScreen({ entry, label }: { entry: PausedFlag; label: string }) {
  const t = useT();
  const { refetch, fetching } = usePausedFlags();
  const time = maintenanceTime(entry.expected_back_at ?? null);

  return (
    <Container className="py-20 text-center">
      <div role="status" className="mx-auto max-w-lg">
        <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-amber-50">
          <PauseCircle className="size-6 text-amber-600" aria-hidden />
        </span>
        <h1 className="mt-4 text-2xl font-bold text-ink text-balance">
          {t('system.paused.title', { feature: label })}
        </h1>
        <p className="mx-auto mt-3 max-w-md text-muted">{entry.message}</p>
        <p className="mt-4 text-sm font-medium text-ink">
          {time ? t('system.paused.back_by', { time }) : t('system.paused.back_shortly')}
        </p>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted">{t('system.paused.body')}</p>
        <Button variant="outline" className="mt-6" disabled={fetching} onClick={() => void refetch()}>
          <RotateCw className="mr-1.5 size-4" aria-hidden />
          {t('system.paused.check')}
        </Button>
      </div>
    </Container>
  );
}

/**
 * The same news for a page a switch covers only part of - shipping rates still
 * save while Shiprocket is paused, and a domain already owned still connects
 * while buying is paused. A strip at the top, not a wall.
 */
export function PausedFlagBanner({ flags }: { flags: readonly string[] }) {
  const t = useT();
  const { paused } = usePausedFlags();
  const entries = flags.map((flag) => paused(flag)).filter((entry) => entry !== null);
  if (entries.length === 0) return null;

  return (
    <div className="mb-4 space-y-2">
      {entries.map((entry) => {
        const time = maintenanceTime(entry.expected_back_at ?? null);
        return (
          <div
            key={entry.key}
            role="status"
            className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <PauseCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <p>
              {entry.message}{' '}
              <span className="font-medium">
                {time ? t('system.paused.back_by', { time }) : t('system.paused.back_shortly')}
              </span>
            </p>
          </div>
        );
      })}
    </div>
  );
}

/** A layout route: the strip above whatever page it wraps. */
export function PausedFlagStrip({ flags }: { flags: readonly string[] }) {
  return (
    <>
      <PausedFlagBanner flags={flags} />
      <Outlet />
    </>
  );
}
